
import { Button,Input } from 'components/atoms/index';
import { message } from 'antd';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from '@/icons';
import { vars } from 'style/theme.css'
import { useNavigate } from 'react-router-dom';
import Pagination from 'components/atoms/Pagination/Pagination';
import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import { academyTeacherApi, academyRegistrationApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';

interface TeacherRow {
  id: number;
  name: string;
  birthDate: string;
  status: '정상' | '퇴원';
  registrationDate: string;
  withdrawnAt?: string | null;
}
  
  // 날짜 포맷터 (yyyy-MM-dd[THH:mm:ss] → yyyy.MM.dd)
  function formatYmd(s?: string | null) {
    if (!s) return '';
    const only = s.split('T')[0];
    const parts = only.split('-');
    if (parts.length === 3) return `${parts[0]}.${parts[1]}.${parts[2]}`;
    return s;
  }

  // 날짜시간 포맷터 (yyyy-MM-ddTHH:mm:ss[.SSS] → yyyy-MM-dd HH:mm)
  function formatYmdHm(s?: string | null) {
    if (!s) return '';
    const [datePart, timePartRaw] = s.split('T');
    if (!timePartRaw) return datePart || '';
    const timePart = timePartRaw.replace('Z', '');
    const [hh, mm] = timePart.split(':');
    if (!hh || !mm) return datePart || '';
    return `${datePart} ${hh}:${mm}`;
  }

  // 컬럼 렌더링용 메타 정보
  const columns = [
    { key: 'id'     as const, render: (row: TeacherRow) => row.id },
    { key: 'name'   as const, render: (row: TeacherRow) => row.name },
    { key: 'birthDate' as const, render: (row: TeacherRow) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">생년월일</span>
        <span>{row.birthDate || '-'}</span>
      </div>
    ) },
    { key: 'status' as const, render: (row: TeacherRow) => renderStatus(row.status, row.withdrawnAt) },
    { key: 'registrationDate' as const, render: (row: TeacherRow) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">등록일</span>
        <span>{row.registrationDate}</span>
      </div>
    ) },
  ]
  
  function renderStatus(status: TeacherRow['status'], withdrawnAt?: string | null) {
    switch (status) {
      case '정상':
        return <span className="font-medium text-green-600">정상</span>
      case '퇴원':
        return (
          <span className="font-medium text-red-600">
            퇴원{withdrawnAt ? `(${formatYmdHm(withdrawnAt)})` : ''}
          </span>
        )
      default:
        return <span>-</span>
    }
  }

// API 데이터 상태

export default function TeacherTable() {
  const navigate = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [rows, setRows] = useState<TeacherRow[]>([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const { academyCode: sessionAcademyCode, status: sessionStatus } = useSessionSlug();
  const academyCode = sessionAcademyCode || '';
  const [searchText, setSearchText] = useState('');

  const fetchList = async (p = page, s = size) => {
    const codeToUse = (academyCode).trim();
    if (!codeToUse) return;
    try {
      const res: any = await academyTeacherApi.list({ academyCode: codeToUse, q: searchText, page: p, size: s });
      const pageData = res?.data || res;
      const content = pageData?.content || [];
      const mapped: TeacherRow[] = content.map((it: any) => ({
        id: it.id,
        name: it.name,
        birthDate: formatYmd(it.birthDate) || '-',
        status: it.status === '정상' ? '정상' : '퇴원',
        registrationDate: formatYmd(it.registrationDate) || '-',
        withdrawnAt: it.withdrawnAt || null,
      }));
      setRows(mapped);
      setTotal(pageData?.totalElements ?? mapped.length);
      setPage(pageData?.number ?? p);
      setSize(pageData?.size ?? s);
    } catch (e: any) {
      const msg = e?.response?.data?.message || '강사 목록 조회 실패';
      message.error(msg);
    }
  };

  useEffect(() => {
    if (sessionStatus !== 'ready' || !academyCode) return;
    fetchList(0, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academyCode, sessionStatus]);

  const handleRowClick = (teacherId: number) => {
    navigate(`/academy/teacher/detail/${teacherId}`);
  };

  const handleCreateAccount = () => {
    setShowCreateDialog(true);
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
  };

  const [formState, setFormState] = useState({ name: '', loginId: '', password: '', birthDate: '' });
  const [loginIdStatus, setLoginIdStatus] = useState<'idle'|'checking'|'available'|'duplicate'>('idle');
  const [loginIdMessage, setLoginIdMessage] = useState<string>('');
  const checkTimerRef = useRef<any>(null);
  const birthKeyBlockRef = useRef(false);

  const triggerCheckLoginId = async (value: string) => {
    const loginId = (value || '').trim();
    if (!loginId) { setLoginIdStatus('idle'); setLoginIdMessage(''); return; }
    setLoginIdStatus('checking');
    setLoginIdMessage('');
    try {
      const res: any = await academyRegistrationApi.checkLoginId(loginId);
      const ok = res?.data === true || res === true;
      setLoginIdStatus(ok ? 'available' : 'duplicate');
      setLoginIdMessage(res?.message || (ok ? '사용 가능한 아이디입니다.' : '이미 사용중인 아이디입니다.'));
    } catch {
      setLoginIdStatus('idle');
      setLoginIdMessage('');
    }
  };

  const handleLoginIdChange = (e: any) => {
    const value = e.target.value;
    setFormState(v => ({ ...v, loginId: value }));
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    checkTimerRef.current = setTimeout(() => {
      if ((value || '').trim().length >= 3) {
        triggerCheckLoginId(value);
      } else {
        setLoginIdStatus('idle');
        setLoginIdMessage('');
      }
    }, 300);
  };

  const handleConfirmCreate = async () => {
    const codeToUse = (academyCode).trim();
    if (!codeToUse) { message.error('학원 정보가 없습니다. 다시 로그인해주세요.'); return; }
    if (!formState.loginId || !formState.password) { return; }
    if (!/^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/.test(formState.password)) {
        message.error('비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.');
        return;
    }
    try {
      // 최종 중복 검사
      const res: any = await academyRegistrationApi.checkLoginId(formState.loginId.trim());
      const ok = res?.data === true || res === true;
      if (!ok) {
        message.error(res?.message || '이미 사용중인 아이디입니다.');
        return;
      }
      await academyTeacherApi.create({ 
        academyCode: codeToUse, 
        name: formState.name, 
        loginId: formState.loginId, 
        password: formState.password,
        birthDate: formState.birthDate,
      });
      message.success('강사 계정이 생성되었습니다.');
      setFormState({ name: '', loginId: '', password: '', birthDate: '' });
      setLoginIdStatus('idle');
      setLoginIdMessage('');
      setShowCreateDialog(false);
      fetchList(0, size);
    } catch (e: any) {
      const msg = e?.response?.data?.message || '강사 계정 생성 실패';
      message.error(msg);
    }
  };

  const formatBirthDate = (raw: string) => {
    const digits = (raw || '').replace(/[^0-9]/g, '').slice(0, 8);
    const y = digits.slice(0, 4);
    const m = digits.slice(4, 6);
    const d = digits.slice(6, 8);
    if (digits.length <= 4) return y;
    if (digits.length <= 6) return `${y}-${m}`;
    return `${y}-${m}-${d}`;
  };

  const handleBirthChange = (e: any) => {
    const formatted = formatBirthDate(e.target.value);
    setFormState(v => ({ ...v, birthDate: formatted }));
  };

  const handleBirthKeyDown = (e: any) => {
    const allowed = [
      'Backspace','Tab','ArrowLeft','ArrowRight','Delete','Home','End'
    ];
    if (allowed.includes(e.key)) return;
    // 숫자만 허용
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      birthKeyBlockRef.current = true;
      return;
    }
    birthKeyBlockRef.current = false;
  };

  return (
        <main className='flex flex-col w-full'>
            <section className='flex flex-col gap-2 items-start pt-3 w-full lg:flex-row lg:justify-between lg:items-center'>
                <Input
                placeholder='검색어를 입력해주세요.'
                className='max-w-[500px]'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e: any) => { if (e.key === 'Enter') fetchList(0, size); }}
                icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
                <Button onClick={handleCreateAccount} >
                    강사 계정 생성
                </Button>
            </section>

            <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
                {/* no_list */}
                {/* <p>리스트가 없습니다.</p> */}
                <div className='w-full'>
                    {/* 테이블 해더 pc 용*/}
                    <ul className={`${styles.academyHeaderList} w-full`}>
                        <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                        <li className={`${styles.headerItem}`}>강사명</li>
                        <li className={`${styles.headerItem} max-w-[200px]`}>생년월일</li>
                        <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>상태</li>
                        <li className={`${styles.headerItem} max-w-[200px]`}>등록일</li>
                    </ul>
                    <div className='flex flex-col gap-4 w-full lg:gap-0'>
                        {rows.map((row, index) => {
                          const globalIndex = page * size + index;
                          const descendingNo = total > 0 ? total - globalIndex : Math.max(1, rows.length - index);
                          return (
                            <ul 
                                key={row.id} 
                                className={`${styles.academyBodyList} lg:shadow-none table-card cursor-pointer hover:bg-gray-50 transition-colors !grid grid-cols-2 lg:!flex`}
                                onClick={() => handleRowClick(row.id)}
                            >
                            {columns.map(({ key, render }) => (
                                <li
                                key={key}
                                className={`${styles.bodyItem} ${
                                    key === 'id' ? 'max-w-[80px] !hidden lg:!flex justify-center' :
                                    key === 'name' ? 'col-span-2 lg:col-span-1 ' :
                                    key === 'birthDate' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                    key === 'status' ? 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center' :
                                    key === 'registrationDate' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                    ''
                                }`}
                                >
                                {key === 'id' ? descendingNo : render(row)}
                                </li>
                            ))}
                            </ul>
                        )})}
                    </div>
                </div>
                    <div className='py-5'>
                <Pagination
                total={total}
                pageSize={size}
                current={page + 1}
                onChange={(p) => fetchList((p as number) - 1, size)}
                />
                </div>
            </section>
            
            {showCreateDialog && (
                <Dialog
                    onCancel={handleCloseDialog}
                    hideFooter={true}
                >
                    <div className="p-6">
                        <h3 className="mb-4 text-lg font-semibold">강사 계정 생성</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700">강사명</label>
                                <Input placeholder="강사명을 입력해주세요" value={formState.name} onChange={(e: any) => setFormState(v => ({ ...v, name: e.target.value }))} />
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700">ID</label>
                                <Input placeholder="아이디를 입력해주세요" value={formState.loginId} onChange={handleLoginIdChange} />
                                {loginIdStatus !== 'idle' && (
                                  <small className={`text-xs ${loginIdStatus === 'available' ? 'text-green-600' : loginIdStatus === 'duplicate' ? 'text-red-600' : 'text-gray-500'}`}>
                                    {loginIdStatus === 'checking' ? '중복 확인 중...' : (loginIdMessage || '')}
                                  </small>
                                )}
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700">비밀번호</label>
                                <Input placeholder="비밀번호를 입력해주세요" type="password" value={formState.password} onChange={(e: any) => setFormState(v => ({ ...v, password: e.target.value }))} />
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700">생년월일</label>
                                <Input
                                  placeholder="YYYY-MM-DD"
                                  value={formState.birthDate}
                                  onKeyDown={handleBirthKeyDown}
                                  onChange={handleBirthChange}
                                  inputMode="numeric"
                                />
                            </div>
                            <div className="flex gap-2 justify-end pt-2">
                              <Button intent="secondary" onClick={handleCloseDialog}>취소</Button>
                              <Button 
                                onClick={handleConfirmCreate}
                                disabled={
                                  !formState.loginId || !formState.password ||
                                  loginIdStatus === 'checking' || loginIdStatus === 'duplicate'
                                }
                              >
                                생성
                              </Button>
                            </div>
                        </div>
                    </div>
                </Dialog>
            )}
        </main>
  )
}